<template>
<collapsible-panel
	:always-open="true"
	:fixed-position="true"
	panel-size="sm">

	<template slot="panel">
		<panel-menu
			:menu-items="bidTabs"
			:active-item="activeTab"
			v-on:itemClick="activateSection">
		</panel-menu>
	</template>

	<template slot="main">

		<div class="d-flex justify-content-between mb-2 ">
			<h2 class="mb-0">
				Project Bid
				<small v-if="$store.project && 'job_num' in $store.project">
					{{ $store.project.job_num }}: {{ $store.project.job_name}}
				</small>
			</h2>

			<div class="d-flex align-items-center">

				<button v-if="!rightPanelVisible" title="Open side panel" style="min-width: 60px;" class="mr-2 btn btn-light" v-on:click="rightPanelVisible = true">
					<i class="fa fa-chevron-left mr-2"></i>
					<i class="fa fa-columns"></i>
				</button>

				<field v-if="rightPanelVisible"
					:params="{
						type: 'btn-group',
						buttons: [
							{
								icon: 'fa-columns',
								value: 'summary',
								title: 'Bid Summary'
							},
							{
								icon: 'fa-sitemap',
								value: 'tree',
								title: 'Bid Structure'
							},
							{
								icon: 'fa-file-pdf',
								value: 'preview',
								title: 'Bid Preview'
							}
						],
						disableNull: true
					}"
					v-model="sidebarView">
				</field>

				<i class="fa fa-times icon-button close-edit ml-3" v-on:click="$router.push('/project/'+$route.params.id+'/bidding')"></i>
			</div>
		</div>

		<div class="project-bid-main-layout">
			<div class="project-bid-content" :class="{ 'with-right-panel': rightPanelVisible }">
				<div class="d-flex justify-content-between mb-2 align-items-center">

					<router-link :to="'/project/'+$route.params.id+'/bidding'">
						<i class="fa fa-arrow-left"></i>
						Back to Bids
					</router-link>

					<div v-if="!component && activeTab == 'Bid Structure' && bid.is_finalized == 0">
						<button class="btn btn-success" v-on:click="addComponent(0)">
							<i class="fa fa-plus"></i>
							Add Category
						</button>
					</div>
				</div>

				<template v-if="activeTab == 'Bid Details'">
					<h3>Bid Details</h3>

					<field label="Bid Name"
						col-class="col-md-4"
						v-model="bid.name"
						:readonly="bid.is_finalized == 1"
						v-on:change="saveBidDetails">
					</field>

					<field label="Revision #"
						col-class="col-md-4"
						v-model="bid.revision_num"
						:readonly="bid.is_finalized == 1"
						v-on:change="saveBidDetails">
					</field>

					<field label="Bid Notes (Top)"
						col-class="stacked"
						:params="{type: 'richtext'}"
						v-model="bid.notes_top"
						:readonly="bid.is_finalized == 1"
						v-on:input="debounce(saveBidDetails,$event,1000)">
						<div class="text-muted">These notes will appear at the top of the submitted bid document</div>
					</field>

					<field label="Bid Notes (Bottom)"
						col-class="stacked"
						:params="{type: 'richtext'}"
						v-model="bid.notes_bottom"
						:readonly="bid.is_finalized == 1"
						v-on:input="debounce(saveBidDetails,$event,1000)">
						<div class="text-muted">These notes will appear at the bottom of the submitted bid document</div>
					</field>

					<field label="Internal Notes"
						col-class="stacked"
						:params="{type: 'textarea'}"
						v-model="bid.internal_notes"
						:readonly="bid.is_finalized == 1"
						v-on:change="saveBidDetails">
						<div class="text-muted">These notes will stay hidden</div>
					</field>

					
				</template>

				<template v-else-if="activeTab == 'Bid Structure'">
					<div v-if="breadcrumb.length > 0">
						<nav>
							<ol class="breadcrumb">
								<li class="breadcrumb-item"><a href="#"><i class="fa fa-arrow-up" v-on:click="upComponent(component)"></i></a></li>
								<li class="breadcrumb-item" :class="{active: index == breadcrumb.length - 1}" v-for="(comp,index) in breadcrumb">
									<a v-if="index != breadcrumb.length - 1" href="#" v-on:click="toComponent(comp,index)"><span v-if="comp.item_number">{{ comp.item_number }}. </span>{{ comp.name }}</a>
									<span v-else><span v-if="comp.item_number">{{ comp.item_number }}. </span>{{comp.name}}</span>
								</li>
							</ol>
						</nav>
					</div>

					<div v-if="!component && bid.is_finalized == 0 && bidComponents.length > 0" class="d-flex justify-content-end mb-2">
						<button type="button"
							class="btn btn-sm"
							:class="sortBidComponentsEnabled ? 'btn-primary' : 'btn-outline-secondary'"
							v-on:click="sortBidComponentsEnabled = !sortBidComponentsEnabled">
							<i class="fa fa-sort mr-1"></i>
							{{ sortBidComponentsEnabled ? 'Done' : 'Reorder' }}
						</button>
					</div>

					<project-bid-component v-if="component"
						:component="component"
						:is-finalized="bid.is_finalized == 1"
						v-on:toComponent="toComponent">
					</project-bid-component>

					<draggable v-else-if="bidComponents.length > 0" 
						v-model="bidComponents" 
						class="list-group mb-2"
						:disabled="!sortBidComponentsEnabled || bid.is_finalized == 1"
						:options="{handle:'.handle'}"
						v-on:end="updateOrder">

						<li v-for="comp in bidComponents"
							class="list-group-item d-flex justify-content-between align-items-center">

							<div v-if="sortBidComponentsEnabled && bid.is_finalized == 0" class="handle mr-2" title="Drag to reorder">
								<i class="fa fa-sort"></i>
							</div>

							<div style="flex-grow: 1;" class="clickable-item" v-on:click="toComponent(comp)">
								<i class="fa fa-link mr-2"></i>
								<span v-if="comp.item_number">{{ comp.item_number }}. </span>{{ comp.name }}
							</div>

						</li>
					</draggable>

					<div v-else>

						<p>No components exist in this bid</p>

					</div>
				</template>

				<compiled-bid-elements v-else-if="activeTab == 'Compiled Elements'"
					:is-finalized="bid.is_finalized == 1"
					:bid-id="bid.id">
				</compiled-bid-elements>

				<project-bid-financial-summary-tab v-else-if="activeTab == 'Financial Summary'"
					:bid-id="bid.id">
					
				</project-bid-financial-summary-tab>

				<template v-else-if="activeTab == 'Display Settings'">
					<h3>Display Settings</h3>

					<field label="Component Detail Depth"
						col-class="col-md-4"
						:params="{
							type: 'select',
							options: [
								{text: 'Lump Sum Only',value: -1},
								{text: 'Summary Only',value: 0},
								{text: '1 Layer',value: 1},
								{text: '2 Layers',value: 2},
								{text: '3 Layers',value: 3}
							]
						}"
						:readonly="bid.is_finalized == 1"
						v-model="bid.display_depth"
						v-on:change="saveBidDetails">
					</field>

					<field label="Hide Prices"
						col-class="col-md-4"
						:params="{
							type: 'boolean',
						}"
						:readonly="bid.is_finalized == 1"
						v-model="bid.hide_prices"
						v-on:change="saveBidDetails">
					</field>
				</template>

				<template v-else-if="activeTab == 'Finalizing'">
					<h3>Finalizing</h3>

					<field label="Submission Date"
						col-class="col-md-4"
						:params="{
							type: 'date'
						}"
						:readonly="bid.is_finalized == 1"
						v-model="bid.issue_date"
						v-on:change="saveBidDetails">
					</field>

					<field v-for="tax in salesTaxes"
						:label="'Charge '+tax.label"
						col-class="col-lg-3 col-sm-4"
						:params="{type:'boolean'}"
						v-model="appliedTaxes[tax.label]"
						v-on:change="salesTaxToggle(tax.label)">
					</field>

				</template>

				<template v-else>
					<div class="d-flex justify-content-between align-items-center mb-2">
						<h3 class="mb-0">Bid Delivery</h3>

						<context-menu :menu-items="deliveryMenuItems"
							v-on:definalize="toggleFinalizeBid(0)"
							v-on:copyToScheduleOfValues="copyToScheduleOfValues">
						</context-menu>
					</div>

					<p v-if="bid.last_issued" class="text-muted">Last sent {{ formatDateTimeToLocal(bid.last_issued) }}</p>

					<div class="d-flex justify-content-between">

						<field label="Bid Total"
							col-class="col-lg-2 col-md-3 col-sm-4"
							:readonly="true"
							:value="grandTotal">
						</field>

						<field label="Bid status"
							col-class="col-auto"
							:params="{type: 'select',options:[
								{text:'Submitted',value:'Submitted'},
								{text:'Pending Approval',value:'Pending Approval'},
								{text:'Accepted',value:'Accepted'},
								{text:'Rejected',value:'Rejected'}
							]}"
							v-model="bid.status"
							v-on:change="updateBidStatus">
						</field>

					</div>

					<div class="d-flex flex-row justify-content-between flex-wrap">
						<div>

							<!--<button class="btn btn-light" v-on:click="deFinalize">
								<i class="fa fa-lock-open"></i>
								De-Finalize for Editing
							</button>-->

							<a :href="bidDLSrc" class="btn btn-light">
								<i class="fa fa-download"></i>
								Download Bid
							</a>

							<a v-if="clientInvoiceSettings && 'invoicePortalLink' in clientInvoiceSettings && clientInvoiceSettings.invoicePortalLink"
								class="btn btn-light" 
								:href="clientInvoiceSettings.invoicePortalLink" 
								target="_blank">

								<i class="fa fa-external-link"></i>

								Go to Client's Invoicing Portal
							</a>

						</div>
						<div>
							<button class="btn btn-primary mr-1" v-on:click="markAsSent">
								<i class="fa fa-paper-plane"></i>
								Mark Bid as Sent
							</button>
							<button class="btn btn-primary" v-on:click="openSendBidModal">
								<i class="fa fa-paper-plane"></i>
								Email Bid
							</button>
						</div>
					</div>

					<activity-log 
						:submitted-log-json="bid.submitted_log_json"
						:seen-log-json="bid.seen_log_json"
						type-name="Bid">
					</activity-log>

					<send-document-modal v-if="$store.project"
						:visible="sendBidModal.visible"
						document-type="Bid"
						:customer="$store.project.Customer"
						:employees="$store.users"
						:multi-customers="$store.project.customer_id > 0 ? false : true"
						:customers="$store.customers"
						v-on:closeModal="sendBidModal.visible = false"
						v-on:send="emailBid">
					</send-document-modal>

				</template>

				<div style="clear: both;"></div>
				<div v-if="bid.is_finalized == 0" class="d-flex justify-content-between align-items-center">
					<div>
						<button v-if="bidTabs.indexOf(activeTab) != 0" class="btn btn-light" v-on:click="prevSection">
							<i class="fa fa-chevron-left"></i>
							Previous
						</button>
					</div>
					<div>
						<button v-if="bidTabs.indexOf(activeTab) != bidTabs.length - 1 && activeTab != 'Finalizing'" class="btn btn-light" v-on:click="nextSection">
							Next
							<i class="fa fa-chevron-right"></i>
						</button>

						<button v-else-if="activeTab == 'Finalizing'" class="btn btn-lg btn-success" v-on:click="toggleFinalizeBid(1)">
							<i class="fa fa-lock"></i>
							Finalize Bid
						</button>
					</div>
				</div>

			</div>
			<div v-if="rightPanelVisible" class="project-bid-right-panel">
				<div class="project-bid-right-panel-header">
					<field
						:params="{
							type: 'btn-group',
							buttons: [
								{ icon: 'fa-columns', value: 'summary', title: 'Bid Summary' },
								{ icon: 'fa-sitemap', value: 'tree', title: 'Bid Structure' },
								{ icon: 'fa-file-pdf', value: 'preview', title: 'Bid Preview' }
							],
							disableNull: true
						}"
						v-model="sidebarView">
					</field>
					<i class="fa fa-times icon-button project-bid-right-panel-close" title="Close panel" v-on:click="rightPanelVisible = false"></i>
				</div>
				<div class="project-bid-right-panel-inner">

					<project-bid-summary v-if="sidebarView == 'summary'"
						:bid-prop="bid"
						:show-collapse-button="true"
						:selected-top-component-id="selectedTopLevelComponent ? selectedTopLevelComponent.id : false"
						:selected-component-id="component ? component.id : false"
						:hide-price-adjustment="true"
						:hide-markup="true"
						:hide-spec-number="true"
						v-on:collapseSummary="rightPanelVisible = false">
					</project-bid-summary>

					<project-bid-tree v-else-if="sidebarView == 'tree'"
						:bid-prop="bid"
						:show-collapse-button="true"
						:selected-component-id="component ? component.id : false"
						v-on:collapseTreePanel="rightPanelVisible = false">
					</project-bid-tree>

					<div v-else-if="sidebarView == 'preview'">
						<div class="d-flex justify-content-between align-items-center">

							<h3>
								<i v-on:click="rightPanelVisible = false" class="fa fa-chevron-right icon-button"></i>
								Preview
							</h3>

							<a class="" :href="bidPreviewSrc" target="_blank">View in another tab</a>

						</div>
						
						<pdf-js v-on:click="reloadBidPdf"
							:url="bidPreviewSrc"
							:page="1">
						</pdf-js>
					</div>
				</div>
			</div>
		</div>

		<modal-dialog :modal-visible="addComponentModal.visible"
			:title="addComponentModal.bid_component_id == 0 ? 'Add Category' : 'Add Component'"
			v-on:closeModal="addComponentModal.visible = false"
			v-on:confirm="saveNewComponent">

			<field v-if="addComponentModal.bid_component_id == 0"
				label="Category Name"
				:align-label-right="true"
				col-class="col-auto"
				v-model="addComponentModal.payload.name"
				:params="{type:'text',maxlength: 50}">
			</field>

			<template v-else>
				<field label="Component Name"
					:align-label-right="true"
					col-class="col-md-5"
					v-model="addComponentModal.payload.name"
					:params="{type:'text',maxLength:90}">
				</field>

				<component-detail-fields :values="addComponentModal.payload"
					col-class="col-md-5"
					v-on:change="componentDetailsChange">
				</component-detail-fields>

			</template>

			<template slot="footer">
				<button v-if="addComponentModal.bid_component_id > 0" class="btn btn-light" type="button" v-on:click="copyComponent">
					Copy Component
				</button>
			</template>

		</modal-dialog>

		<modal-dialog :modal-visible="bidAcceptedModal.visible"
			title="Bid Won"
			confirm-button-text="Go to Project Details"
			v-on:closeModal="bidAcceptedModal.visible = false"
			v-on:confirm="goToDetailsTab">

			<p>Congratulations on an accepted bid! This is now a full-fledged project and you should go to the Project Details page to continue setting it up as such.</p>

			<!--
				<field label="Customer"
					col-class="col-sm-4"
					v-model="$store.project.customer_id" 
					:params="{
						type: 'select', 
						options: $store.customerOptions
					}">
				</field>
			-->

		</modal-dialog>

		<modal-dialog :modal-visible="confirmCopyToScheduleOfValuesModal.visible"
			title="Copy Bid to Schedule of Values"
			confirm-button-text="Proceed"
			button-class="btn-primary"
			:working="confirmCopyToScheduleOfValuesModal.working"
			v-on:closeModal="confirmCopyToScheduleOfValuesModal.visible = false"
			v-on:confirm="confirmCopyToScheduleOfValues">

			Would you like to create a billing schedule of values from this bid? This will add schedule of value tasks to any previously existing tasks in your schedule of values.

		</modal-dialog>

		<modal-dialog :modal-visible="componentCopyModal.visible"
			title="Copy Component"
			confirm-button-text="Copy"
			button-class="btn-primary"
			v-on:closeModal="componentCopyModal.visible = false"
			v-on:confirm="confirmCopyComponent">

			<field v-if="componentCopyModal.componentSelected"
				label="Component Selected"	
				col-class="col-auto"
				:value="componentCopyModal.componentSelected.item_number + ': ' + componentCopyModal.componentSelected.name"
				:readonly="true">
			</field>
			<bid-component-picker
				:depth="10"
				:bid-id="bid.id" 
				:project-id="$store.project.id"
				v-on:componentSelected="copyComponentSelected">
			</bid-component-picker>

		</modal-dialog>
		
	</template>
</collapsible-panel>
</template>
<script type="text/javascript">
import CollapsiblePanel from "@/layout_fragments/CollapsiblePanel"
import PanelMenu from "@/components/PanelMenu"
import ProjectBidSummary from "@/pages/project/project_tabs/bidding/components/ProjectBidSummary"
import ProjectBidTree from "@/pages/project/project_tabs/bidding/components/ProjectBidTree"
import ProjectBidFinancialSummaryTab from "@/pages/project/project_tabs/bidding/components/ProjectBidFinancialSummaryTab"

import TableList from "@/components/TableList"
import Widget from "@/components/Widget"
import ModalDialog from "@/components/ModalDialog"
import ProjectBidComponent from "@/pages/project/project_tabs/bidding/components/ProjectBidComponent"
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"
import CompiledBidElements from "@/pages/project/project_tabs/bidding/components/CompiledBidElements"
import PdfJs from "@/components/PdfJs"
import SendDocumentModal from "@/components/SendDocumentModal"

import ComponentDetailFields from "@/pages/project/project_tabs/bidding/components/ComponentDetailFields"
import BidComponentPicker from "@/components/super_components/BidComponentPicker"

import ActivityLog from "@/components/ActivityLog"

import ContextMenu from "@/layout_fragments/ContextMenu"

import draggable from 'vuedraggable'

import ProjectBidMixins from "@/mixins/ProjectBidMixins.js"

	
export default {
	name: "ProjectBid",
	components: {
		CollapsiblePanel,
		ProjectBidComponent,
		ModalDialog,
		Field,
		FormGroup,
		ProjectBidSummary,
		ProjectBidTree,
		PanelMenu,
		CompiledBidElements,
		ProjectBidFinancialSummaryTab,
		draggable,
		PdfJs,
		ContextMenu,
		SendDocumentModal,
		ComponentDetailFields,
		ActivityLog,
		BidComponentPicker,
		TableList,
		Widget
	},
	mixins: [ProjectBidMixins],
	data() {
		return {
			bidComponents: [],
			component: null,
			selectedTopLevelComponent: null,
			breadcrumb: [],
			sortBidComponentsEnabled: false,
			summaryComponent: 0,
			clientInvoiceSettings: null,
			addComponentModal: {
				visible: false,
				payload: {
					name: null,
					spec_number: null,
					description: null,
					item_number: null
				},
				bid_component_id: null
			},
			sendBidModal: {
				visible: false,
				customer: []
			},
			bidAcceptedModal: {
				visible: false
			},
			confirmCopyToScheduleOfValuesModal: {
				visible: false
			},
			componentCopyModal: {
				visible: false,
				componentSelected: null
			},
			activeTab: "Bid Details",
			rightPanelVisible: false,
			sidebarView: "summary",
			previewRefresher: 1,
			previewExpanded: false,
			appliedTaxes: [],
		}
	},
	watch: {
		'$store.project'() {
			this.fetchAndSetBid()
		}
	},
	computed: {
		bidTabs() {
			let tabs = [
				'Bid Details',
				'Bid Structure',
				'Compiled Elements',
				'Financial Summary',
				'Display Settings',
			]

			if (this.bid.is_finalized == 1)
				tabs.push("Delivery")
			else
				tabs.push('Finalizing')

			return tabs
		},
		bidPreviewSrc() {
			let url = window.apiBase+"JobBid/pdf?slug="+this.bid.slug+"&token="+this.$store.sessionToken+"&humanReadable=true&v="+this.previewRefresher

			if (this.bid && this.bid.display_depth != null && this.bid.display_depth !== "")
				url += "&display_depth="+this.bid.display_depth

			return url
		},
		bidDLSrc() {
			return window.apiBase+"JobBid/pdf?slug="+this.bid.slug+"&token="+this.$store.sessionToken+"&humanReadable=true&download=true&v="+this.previewRefresher
		},
		showBidBuildPanel() {
			let tabsWithBuildPanel = [
				"Bid Details",
				"Bid Structure",
				"Compiled Elements"
			]

			return tabsWithBuildPanel.find(tab => tab == this.activeTab)
		},
		grandTotal() {
			let grandTotal = +this.bid.grand_total

			return grandTotal.toCurrency()
		},
		deliveryMenuItems() {
			let items = [
				{
					icon: 'fa-lock-open',
					text: 'De-finalize for Editing',
					clickFunction: 'definalize'
				}
			]

			if (this.bid && this.bid.status == 'Accepted') {
				items.push({
					text: 'Create Schedule of Values from Bid',
					clickFunction: 'copyToScheduleOfValues'
				})
			}

			return items
		}
	},
	methods: {
		activateSection(item) {
			this.activeTab = item

			if (this.activeTab == 'Bid Structure' && this.component)
				this.fetchComponent(this.component.slug)
			else if (this.activeTab == 'Finalizing' || this.activeTab == 'Display Settings')
				this.sidebarView = 'preview'
		},
		setBid() {
			if (this.bid.is_finalized == 1)
				this.activeTab = "Delivery"

			if (this.bid && "JobBidComponent" in this.bid && this.bid.JobBidComponent)
				this.bidComponents = this.bid.JobBidComponent
			
			if (this.bid.is_finalized == 1)
				this.sortBidComponentsEnabled = false

			this.setSalesTax()
		},
		nextSection() {
			let currentIndex = this.bidTabs.indexOf(this.activeTab)

			this.activeTab = this.bidTabs[currentIndex + 1]
		},
		prevSection() {
			let currentIndex = this.bidTabs.indexOf(this.activeTab)

			this.activeTab = this.bidTabs[currentIndex - 1]
		},
		upComponent(currentComponent) {
			this.breadcrumb.splice(this.breadcrumb.length - 1,1)

			if (currentComponent.bid_component_id > 0)
				this.fetchComponent(currentComponent.bid_component_id)
			else {
				this.selectedTopLevelComponent = null
				this.component = null
			}
		},
		toComponent(comp,index) {
			this.breadcrumb.splice(index,this.breadcrumb.length - index)

			this.breadcrumb.push(comp)

			this.fetchComponent(comp.slug)
		},
		addComponent(parentComponentId) {
			this.addComponentModal.visible = true
			this.addComponentModal.payload = {
				qty: 1,
				name: "",
				description: "",
				item_number: "",
				spec_number: ""
			}
			this.addComponentModal.bid_component_id = parentComponentId
		},
		openBidSummary() {
			this.rightPanelVisible = true
			this.showBidSummary = true
			this.showBidTree = false
		},
		openBidTree() {
			this.rightPanelVisible = true
			this.showBidSummary = false
			this.showBidTree = true
		},
		componentDetailsChange(fields) {
			let vm = this

			Object.keys(fields).forEach(key => {
				if (key != 'name')
					vm.addComponentModal.payload[key] = fields[key]
			})			
		},
		copyComponent() {
			this.componentCopyModal.visible = true
			this.componentCopyModal.componentSelected = null
		},
		copyComponentSelected(component) {
			this.componentCopyModal.componentSelected = component
		},
		confirmCopyComponent() {
			let vm = this

			let payload = {
				parentComponentId: this.component.id,
				componentId: this.componentCopyModal.componentSelected.id
			}

			this.postData("JobBidComponent/clone-component",payload).then(response => {
				if (response.success) {
					vm.alertSaved("Component cloned successfully")
					this.componentCopyModal.visible = false
					this.addComponentModal.visible = false
					this.fetchComponent(vm.component.id)
					this.fetchAndSetBid()
				}
			})
		},	
		saveNewComponent() {
			let vm = this

			let fields = ["name","description","item_number","spec_number","qty","qty_unit","is_reverse_calculated"]

			let payload = {
				bid_id: this.bid.id
			}

			fields.forEach(key => {
				payload[key] = vm.addComponentModal.payload[key]
			})

			if (this.addComponentModal.bid_component_id > 0)
				payload.bid_component_id = this.addComponentModal.bid_component_id

			this.postData("JobBidComponent/save",payload,true).then(response => {
				if (response.success) {
					vm.fetchAndSetBid()

					if (vm.addComponentModal.bid_component_id > 0)
						vm.fetchComponent(vm.addComponentModal.bid_component_id)

					vm.addComponentModal.visible = false
				}
			})
		},
		updateBidStatus(statusValue) {
			let vm = this

			let payload = {
				id: this.bid.id,
				status: statusValue
			}

			this.postData("JobBid/save",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Bid status set to " + statusValue)
					if (statusValue == 'Accepted') {
						vm.postData("Job/save",{id:vm.$store.project.id,is_lead:0})
						vm.bidAcceptedModal.visible = true
					}
				}
			})
		},
		updateOrder() {
			let payload = {
				records: JSON.stringify(this.bidComponents.map((comp,index) => {
					return {
						order_index: index,
						id: comp.id
					}
				}))
			}

			this.postData("JobBidComponent/save-batch",payload)
		},
		setSalesTax() {
			let vm = this

			this.appliedTaxes = {}

			let appliedSalesTaxObj = {}
			try {
				appliedSalesTaxObj = JSON.parse(this.bid.applied_sales_tax_json)
			}
			catch (e) {}

			this.salesTaxes.forEach(tax => {
			if (tax.label in appliedSalesTaxObj)
				vm.appliedTaxes[tax.label] = 1
		  })
		},
		salesTaxToggle(taxKey) {
			let vm = this

			this.$nextTick(() => {

				let appliedSalesTaxObj = {}
				let companySalesTax = {}
				try {
					appliedSalesTaxObj = JSON.parse(vm.bid.applied_sales_tax_json)
				}
				catch (e) {}
				try {
					companySalesTax = JSON.parse(vm.$store.company.CompanySetting.taxes_json)
				}
				catch (e) {}

				if (!vm.appliedTaxes[taxKey])
					delete appliedSalesTaxObj[taxKey]
				else if (taxKey in companySalesTax)
					appliedSalesTaxObj[taxKey] = companySalesTax[taxKey]

				vm.bid.applied_sales_tax_json = JSON.stringify(appliedSalesTaxObj)

				vm.saveBidDetails()
			})
		},
		saveBidDetails() {
			let vm = this

			this.$nextTick(() => {

				let fields = ["name","notes_top","notes_bottom","internal_notes","issue_date","display_depth","revision_num","hide_prices","applied_sales_tax_json"]

				let payload = {}
				fields.forEach(field => {
					payload[field] = vm.bid[field]
				})

				if ("id" in vm.bid && vm.bid.id)
					payload.id = vm.bid.id

				this.postData("JobBid/save",payload,true).then(response => {
					if (response.success) {
						vm.alertSaved("Bid saved")
						vm.previewRefresher++
					}
				})
			})
		},
		toggleFinalizeBid(finalize) {
			let vm = this

			let params = {
				id: this.bid.id,
				is_finalized: finalize
			}

			this.postData("JobBid/save",params,true).then(response => {
				if (response.success) {
					vm.bid.is_finalized = finalize
					if (finalize)
						vm.activeTab = 'Delivery'
					else
						vm.activeTab = 'Finalizing' 
				}
			})
		},
		/*
		saveCustomer() {
			let vm = this

			let params = {
				id: this.$store.project.id,
				customer_id: this.$store.project.customer_id
			}

			this.postData("Job/save",params,true).then(response => {
				if (response.success) {
					vm.bidAcceptedModal.visible = false
					vm.alertSaved("Customer saved")
				}
			})
		},
		*/
		goToDetailsTab() {
			this.$router.push("/project/" + this.$store.project.slug + "/details")
		},
		markAsSent() {
			let vm = this

			let payload = {
				id: this.bid.id,
				last_issued: this.moment.utc().format("YYYY-MM-DD H:mm:ss")
			}

			this.postData("JobBid/save",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Bid marked as sent!")
				}
			})
		},
		openSendBidModal() {
			let vm = this

			if (!this.$store.users || this.$store.users.length == 0)
				this.fetchUsers(false,false,["id","slug","email","first_name","last_name"])

			if (this.$store.project.customer_id == 0)
				this.fetchCustomers(false,false)

			if ("Customer" in this.$store.project && this.$store.project.Customer && (!("SubContacts" in this.$store.project.Customer) || !this.$store.project.Customer.SubContacts)) {
				//need to fetch the sub-contacts and append it
				let params = {
					include: [
						"Contact"
					]
				}

				this.getJSON("Contact/find/"+this.$store.project.Customer.slug,params).then(response => {
					if (response.success)
						vm.$store.project.Customer = response.data
				})
			}

			this.sendBidModal.visible = true
		},
		emailBid(recipients) {
			let vm = this

			let additional = recipients.additional

			let employees = recipients.employeeRecipients.map(id => {
				let emp = vm.$store.users.find(user => user.id == id)

				if (emp)
					return emp.email

				return null
			})
			
			additional += ","+employees.join(",")

			let payload = {
				bid_id: this.bid.id,
				additional,
				recipients: recipients.recipients
			}

			this.postData("JobBid/send",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Bid sent!")
					vm.sendBidModal.visible = false
				}
			})
		},
		copyToScheduleOfValues() {
			this.confirmCopyToScheduleOfValuesModal.visible = true
			this.confirmCopyToScheduleOfValues.working = false
			this.fetchAllComponents(this.bid.id)
		},
		confirmCopyToScheduleOfValues() {
			this.confirmCopyToScheduleOfValuesModal.working = true

			let vm = this

			let job_id = this.$store.project.id

			//iterate through second-layer items
			let records = this.renderJobTaskPayloadFromBid(this.renderNestedBid())

			if (records.length > 0) {
				let payload = {
					records: JSON.stringify(records)
				}

				this.postData("JobTask/save-batch",payload,true).then(response => {
					this.confirmCopyToScheduleOfValuesModal.working = false
					this.confirmCopyToScheduleOfValuesModal.visible = false
					vm.alertSuccess(records.length + " items added to the Schedule of Values!")
				})
			}
			else
				vm.alertFail("There are no bid components!")
		},
		reloadBidPdf() {
			let vm = this
			this.setSessionToken().then(response => {
				vm.previewRefresher++
			})
		},
		fetchAndSetBid() {
			let vm = this
			this.fetchBid(this.$route.params.tag).then(response => {
				if (response.success) {
					vm.setBid()
					vm.$store.bidComponentsFlat = []
					vm.fetchAllComponents(vm.bid.id)
					vm.fetchBidObjects(vm.bid.id)
				}
			})
		},
		fetchComponent(idOrSlug) {
			let vm = this

			let params = {
				include: [
					"JobBidComponentObject",
					"JobBidComponent"
				]
			}

			this.getJSON("JobBidComponent/find/"+idOrSlug,params).then(response => {
				if (response.success) {
					vm.component = response.data

					if (vm.component.bid_component_id == 0)
						vm.selectedTopLevelComponent = response.data
				}
			})
		}
	},
	created() {
		let vm = this
		this.fetchAndSetBid()

		this.$bus.$on("addComponent",(parentComponentId) => {
			vm.addComponent(parentComponentId)
		})

		this.$bus.$on("componentDeleted",(currentComponent) => {
			vm.upComponent(currentComponent)
			vm.fetchAndSetBid()
		})
	}
}
</script>

<style scoped>
.project-bid-main-layout {
	position: relative;
}

.project-bid-content {
	min-width: 0;
	padding-bottom: 24px;
}

.project-bid-content.with-right-panel {
	margin-right: 580px;
}

/* Fixed right sidebar (same top offset and z-index as collapsible panel) */
.project-bid-right-panel {
	position: fixed;
	top: var(--collapsible-panel-fixed-top, 93px);
	right: 0;
	bottom: 0;
	width: 580px;
	z-index: 20;
	background-color: white;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.project-bid-right-panel-header {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: 8px 10px;
	background-color: white;
	flex-shrink: 0;
}

.project-bid-right-panel-header .field {
	margin-bottom: 0;
}

.project-bid-right-panel-close {
	flex-shrink: 0;
	margin-left: 8px;
}

.project-bid-right-panel-inner {
	flex: 1;
	min-height: 0;
	padding: 10px;
	overflow-y: auto;
	overflow-x: auto;
}

@media (max-width: 1199px) {
	.project-bid-content.with-right-panel {
		margin-right: 0;
	}

	.project-bid-main-layout {
		display: flex;
		flex-direction: column;
	}

	.project-bid-content {
		flex: 0 0 auto;
	}

	.project-bid-right-panel {
		position: relative;
		top: auto;
		right: auto;
		bottom: auto;
		width: 100%;
		max-width: none;
	}
}

.close-edit {

	right: 0;
	z-index: 1;
}

.handle {
	cursor: grab;
	touch-action: none;
}

.clickable-item {
	cursor: pointer;
}

.clickable-item:hover {
	opacity: 0.7
}

.totals-value {
	font-size: 18pt;
	text-align: center;
}
</style>